import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import { AgentService, type CreateJiraTicketResult } from '../../../services/agent/agentService';
import { useAuth } from '../../../hooks/useAuth';
import { drawerPrimaryActionSx, frostedDrawerHeaderSx, frostedDrawerPaperSx } from '../../../theme/componentStyles';
import { diagnosticsCollector } from '../../../services/diagnostics/diagnosticsCollector';
import { buildBugTicketDescription } from '../../../services/diagnostics/bugTicketBuilder';
import {
  BUG_REPORT_ATTACHMENT_CONFIG,
  coerceServerMaxUploadBytes,
  formatAttachmentMaxLabel,
  normalizeAttachmentExtensions,
  validateBugReportAttachment,
} from './bugReportAttachmentConfig';

import { Icon } from '@liftango/ui-icons';
interface BugReportDrawerProps {
  open: boolean;
  onClose: () => void;
  activeLayerId?: number | null;
  mapZoom?: number;
}

type UploadStatus = 'pending' | 'uploading' | 'uploaded' | 'failed';

interface PendingAttachment {
  localId: string;
  file: File;
  status: UploadStatus;
  fileId?: string;
  error?: string;
}

export const BugReportDrawer: React.FC<BugReportDrawerProps> = ({
  open,
  onClose,
  activeLayerId,
  mapZoom,
}) => {
  const { t } = useTranslation('map');
  const bugDrawerZIndex = 2300;
  const { user } = useAuth();
  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateJiraTicketResult | null>(null);
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [supportedExtensions, setSupportedExtensions] = useState<string[]>(
    BUG_REPORT_ATTACHMENT_CONFIG.supportedAttachmentExtensions as unknown as string[]
  );
  const [maxAttachmentSizeBytes, setMaxAttachmentSizeBytes] = useState(
    BUG_REPORT_ATTACHMENT_CONFIG.maxAttachmentSizeBytes
  );
  const [attachmentLimitsFromFallback, setAttachmentLimitsFromFallback] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const agentService = useMemo(() => new AgentService(), []);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const readableFieldSx = {
    '& .MuiOutlinedInput-root': {
      bgcolor: 'common.white',
      color: 'text.primary',
      '& input::placeholder, & textarea::placeholder': {
        color: 'text.secondary',
        opacity: 1,
      },
      '& fieldset': {
        borderColor: 'divider',
      },
      '&:hover fieldset': {
        borderColor: 'text.secondary',
      },
      '&.Mui-focused fieldset': {
        borderColor: 'actionBrand.primary',
      },
    },
    '& .MuiInputLabel-root': {
      color: 'text.secondary',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: 'text.primary',
    },
  } as const;

  const addAttachments = (selectedFiles: File[]) => {
    if (selectedFiles.length === 0) {
      return;
    }

    const next: PendingAttachment[] = [];
    const invalidMessages: string[] = [];
    selectedFiles.forEach((file) => {
      const validationError = validateBugReportAttachment(file, supportedExtensions, maxAttachmentSizeBytes);
      if (validationError) {
        invalidMessages.push(validationError);
        return;
      }
      next.push({
        localId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        status: 'pending',
      });
    });

    setAttachments((prev) => [...prev, ...next]);
    if (invalidMessages.length > 0) {
      setError(invalidMessages.join('; '));
    }
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    setSupportedExtensions([...(BUG_REPORT_ATTACHMENT_CONFIG.supportedAttachmentExtensions as unknown as string[])]);
    setMaxAttachmentSizeBytes(BUG_REPORT_ATTACHMENT_CONFIG.maxAttachmentSizeBytes);
    setAttachmentLimitsFromFallback(false);

    let mounted = true;
    const fetchUploadCapabilities = async () => {
      try {
        const capabilities = await agentService.getUploadCapabilities();
        if (!mounted) {
          return;
        }
        const maxBytes = coerceServerMaxUploadBytes(
          capabilities.max_upload_file_bytes,
          BUG_REPORT_ATTACHMENT_CONFIG.maxAttachmentSizeBytes
        );
        setMaxAttachmentSizeBytes(maxBytes);
        if (Array.isArray(capabilities.supported_extensions)) {
          const normalized = normalizeAttachmentExtensions(capabilities.supported_extensions);
          if (normalized.length > 0) {
            setSupportedExtensions(normalized);
          }
        }
        setAttachmentLimitsFromFallback(false);
      } catch {
        if (mounted) {
          setAttachmentLimitsFromFallback(true);
        }
      }
    };

    void fetchUploadCapabilities();
    return () => {
      mounted = false;
    };
  }, [agentService, open]);

  const handleSelectAttachments = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files ?? []);
    addAttachments(selectedFiles);

    if (event.target) {
      event.target.value = '';
    }
  };

  const handleDropAttachments: React.DragEventHandler<HTMLDivElement> = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
    addAttachments(Array.from(event.dataTransfer.files ?? []));
  };

  const handleDragOverAttachments: React.DragEventHandler<HTMLDivElement> = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isDragOver) {
      setIsDragOver(true);
    }
  };

  const handleDragLeaveAttachments: React.DragEventHandler<HTMLDivElement> = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleWindowPaste = (event: ClipboardEvent) => {
      const clipboardItems = Array.from(event.clipboardData?.items ?? []);
      if (clipboardItems.length === 0) {
        return;
      }

      const pastedFiles = clipboardItems
        .filter((item) => item.kind === 'file')
        .map((item) => item.getAsFile())
        .filter((file): file is File => Boolean(file))
        .map((file) => {
          if (file.name) {
            return file;
          }

          const extensionByType: Record<string, string> = {
            'image/png': 'png',
            'image/jpeg': 'jpg',
            'image/webp': 'webp',
          };
          const extension = extensionByType[file.type] ?? 'png';
          return new File([file], `pasted-screenshot-${Date.now()}.${extension}`, {
            type: file.type || 'image/png',
          });
        });

      if (pastedFiles.length > 0) {
        event.preventDefault();
        addAttachments(pastedFiles);
      }
    };

    document.addEventListener('paste', handleWindowPaste);
    return () => {
      document.removeEventListener('paste', handleWindowPaste);
    };
  }, [open, supportedExtensions, maxAttachmentSizeBytes]);

  const removeAttachment = (localId: string) => {
    setAttachments((prev) => prev.filter((attachment) => attachment.localId !== localId));
  };

  const uploadPendingAttachments = async (): Promise<string[]> => {
    const fileIds: string[] = [];
    for (const attachment of attachments) {
      if (attachment.status === 'uploaded' && attachment.fileId) {
        fileIds.push(attachment.fileId);
        continue;
      }

      setAttachments((prev) =>
        prev.map((item) =>
          item.localId === attachment.localId ? { ...item, status: 'uploading', error: undefined } : item
        )
      );
      try {
        const uploaded = await agentService.uploadFile(attachment.file);
        fileIds.push(uploaded.file_id);
        setAttachments((prev) =>
          prev.map((item) =>
            item.localId === attachment.localId
              ? { ...item, status: 'uploaded', fileId: uploaded.file_id, error: undefined }
              : item
          )
        );
      } catch (uploadError) {
        const uploadMessage =
          uploadError instanceof Error ? uploadError.message : t('bugReport.errorUploadFailed', { name: attachment.file.name });
        setAttachments((prev) =>
          prev.map((item) =>
            item.localId === attachment.localId
              ? { ...item, status: 'failed', error: uploadMessage }
              : item
          )
        );
        throw new Error(uploadMessage);
      }
    }
    return fileIds;
  };

  const handleSubmit = async () => {
    if (!title.trim() || !details.trim()) {
      setError(t('bugReport.errorMissingFields'));
      return;
    }

    setError(null);
    setResult(null);
    setIsSubmitting(true);
    diagnosticsCollector.addBreadcrumb('bug_report', 'submit_started', {
      attachment_count: attachments.length,
      title_length: title.trim().length,
      details_length: details.trim().length,
    });

    try {
      const now = new Date().toISOString();
      const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
      const fileIds = await uploadPendingAttachments();
      const description = buildBugTicketDescription({
        details,
        reportedAt: now,
        currentUrl,
        activeLayerId,
        mapZoom,
        reporterName: user?.fullName || user?.email || 'unknown',
        reporterUserId: user?.userId || 'unknown',
        diagnostics: diagnosticsCollector.getSnapshot(),
      });

      const created = await agentService.createJiraTicket(
        `[Atlas] ${title.trim()}`,
        description,
        'CAB',
        'Bug',
        fileIds
      );
      setResult(created);
      setTitle('');
      setDetails('');
      setAttachments([]);
      diagnosticsCollector.addBreadcrumb('bug_report', 'submit_succeeded', {
        ticket_key: created.ticket_key,
        attachment_count: fileIds.length,
      });
    } catch (submitError) {
      const message = submitError instanceof Error ? submitError.message : t('bugReport.errorSubmitFallback');
      setError(message);
      diagnosticsCollector.addError('custom', message, {
        category: 'bug_report_submission',
        context: {
          attachment_count: attachments.length,
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        zIndex: bugDrawerZIndex,
      }}
      PaperProps={{
        sx: (theme) => ({
          width: 460,
          maxWidth: '90vw',
          zIndex: bugDrawerZIndex,
          ...frostedDrawerPaperSx(theme),
        }),
      }}
    >
      <Box sx={(theme) => ({ ...frostedDrawerHeaderSx(theme), px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' })}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {t('bugReport.title')}
        </Typography>
        <IconButton aria-label={t('bugReport.closeAria')} onClick={onClose}>
          <Icon size={20} name="xmark" />
        </IconButton>
      </Box>

      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
          {t('bugReport.description')}
        </Typography>

        <TextField
          label={t('bugReport.bugTitle')}
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder={t('bugReport.titlePlaceholder')}
          fullWidth
          required
          sx={readableFieldSx}
        />
        <TextField
          label={t('bugReport.details')}
          value={details}
          onChange={(event) => setDetails(event.target.value)}
          placeholder={t('bugReport.detailsPlaceholder')}
          fullWidth
          required
          multiline
          minRows={7}
          sx={readableFieldSx}
        />

        {error && <Alert severity="error">{error}</Alert>}
        {result && (
          <Alert severity="success">
            {t('bugReport.submitted')} <Link href={result.ticket_url} target="_blank" rel="noopener noreferrer">{result.ticket_key}</Link>
          </Alert>
        )}

        <Box
          onDrop={handleDropAttachments}
          onDragOver={handleDragOverAttachments}
          onDragLeave={handleDragLeaveAttachments}
          sx={{
            border: '1px dashed',
            borderColor: isDragOver ? 'actionBrand.primary' : 'divider',
            borderRadius: 1.5,
            p: 1.5,
            bgcolor: isDragOver ? 'rgba(0, 0, 0, 0.02)' : 'transparent',
            transition: 'border-color 120ms ease, background-color 120ms ease',
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t('bugReport.attachEvidence')}
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            hidden
            multiple
            onChange={handleSelectAttachments}
            accept={supportedExtensions.join(',')}
          />
          <Button
            variant="outlined"
            startIcon={<Icon size={20} name="file-arrow-up" />}
            onClick={() => fileInputRef.current?.click()}
            disabled={isSubmitting}
            sx={{ mb: 1 }}
          >
            {t('bugReport.addFiles')}
          </Button>
          <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
            {t('bugReport.dragAndDrop')}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mb: 1,
              color: 'text.primary',
              fontWeight: 500,
            }}
          >
            {t('bugReport.supportedFiles', { types: supportedExtensions.join(', '), size: formatAttachmentMaxLabel(maxAttachmentSizeBytes) })}
          </Typography>
          {attachmentLimitsFromFallback && (
            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
              {t('bugReport.limitsFallback')}
            </Typography>
          )}
          {attachments.length > 0 && (
            <List dense sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              {attachments.map((attachment) => (
                <ListItem
                  key={attachment.localId}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      aria-label={t('bugReport.removeFile', { name: attachment.file.name })}
                      onClick={() => removeAttachment(attachment.localId)}
                      disabled={isSubmitting || attachment.status === 'uploading'}
                    >
                      <Icon name="trash" size={20} />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={attachment.file.name}
                    secondary={attachment.error ?? t('bugReport.fileSize', { size: Math.round(attachment.file.size / 1024) })}
                  />
                  <Chip
                    size="small"
                    label={attachment.status}
                    color={
                      attachment.status === 'uploaded'
                        ? 'success'
                        : attachment.status === 'failed'
                          ? 'error'
                          : attachment.status === 'uploading'
                            ? 'warning'
                            : 'default'
                    }
                    variant="outlined"
                    sx={{ ml: 1 }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting}
          sx={(theme) => drawerPrimaryActionSx(theme)}
          startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isSubmitting ? t('bugReport.submitting') : t('bugReport.submitToCab')}
        </Button>
      </Box>
    </Drawer>
  );
};

export default BugReportDrawer;
